import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRefund } from '../api/refundApi';
import { getMethods } from '../api/methodApi';
import { getReasons } from '../api/reasonApi';
import { getStatuses } from '../api/statusApi';
import { toast } from 'react-toastify';
import { PlusCircle, ArrowLeft, Upload } from 'lucide-react';
import { usePermission } from '../context/PermissionContext';

const RefundCreatePage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [refundReasons, setRefundReasons] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formData, setFormData] = useState({
    method: '',
    reason_id: '',
    user_id: '',
    login_id: '',
    member_name: '',
    refund_name: '',
    amount: '',
    status: '1', // Default status ID
    ticket_id: '',
    banveri: '',
    receipt_image: null
  });
  
  const [receiptPreview, setReceiptPreview] = useState(null);
  
  // Dekont yükleme alanı referansı
  const dropzoneRef = useRef(null);

  // Veritabanından verileri yükle
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Ödeme yöntemlerini her durumda çek
        const methods = await getMethods();
        setPaymentMethods(methods);
        
        // İade sebeplerini çek
        const reasons = await getReasons();
        setRefundReasons(reasons);
        
        // Durumları çek
        const statusList = await getStatuses();
        setStatuses(statusList);
      } catch (error) {
        toast.error(`Veri yüklenirken hata: ${error.message}`);
      }
    };
    
    fetchData();
  }, []);
  
  // Dosya yükleme işlevinin ortak bölümü - useCallback ile optimize edildi
  const handleFileUpload = useCallback((file) => {
    if (!file) return;
    
    // Dosya tipini kontrol et
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Desteklenmeyen dosya formatı. Lütfen PNG, JPG, GIF veya PDF yükleyin.');
      return;
    }
    
    // Dosya boyutunu kontrol et (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'ı aşamaz.');
      return;
    }
    
    setFormData(prevData => ({
      ...prevData,
      receipt_image: file
    }));
    
    // Dosya önizlemesi
    if (file.type.includes('image')) {
      const reader = new FileReader();
      reader.onload = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // PDF dosyaları için basit bir önizleme
      setReceiptPreview('pdf');
    }
    
    // Kullanıcıya dosyanın yüklendiğini bildir
    toast.success(`Dekont yüklendi: ${file.name}`);
  }, []);

  // Ctrl+V ile panodan resim yapıştırma işlevi - useCallback ile optimize edildi
  const handlePaste = useCallback((e) => {
    if (e.clipboardData && e.clipboardData.items) {
      const items = e.clipboardData.items;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          handleFileUpload(file);
          break;
        }
      }
    }
  }, [handleFileUpload]);
  
  useEffect(() => {
    // Sayfa aktifken Ctrl+V olayını dinle
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);
  
  // Sürükle-bırak işlemleri için olay işleyicileri
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.add('bg-gray-50');
    }
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.remove('bg-gray-50');
    }
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dropzoneRef.current) {
      dropzoneRef.current.classList.remove('bg-gray-50');
    }
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  // Form değişiklikleri
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Dosya seçici değişikliği
  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  // Form gönderme
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form doğrulama
    if (!formData.method || !formData.reason_id || !formData.user_id || 
        !formData.login_id || !formData.refund_name || !formData.member_name || 
        !formData.amount || !formData.ticket_id) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Sadece form verilerini kullan
      const formDataToSend = {
        method: formData.method,
        reason_id: formData.reason_id,
        user_id: formData.user_id,
        login_id: formData.login_id,
        member_name: formData.member_name,
        refund_name: formData.refund_name,
        amount: formData.amount,
        status: formData.status,
        ticket_id: formData.ticket_id,
        banveri: formData.banveri,
        receipt_image: formData.receipt_image
      };
      
      // İade oluştur
      const createdRefund = await createRefund(formDataToSend);
      
      toast.success('İade başarıyla oluşturuldu');
      navigate('/refunds');
    } catch (error) {
      toast.error(`İade oluşturulurken hata: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Yeni İade Oluştur</h1>
          <p className="text-gray-600">Yeni bir iade işlemi oluşturmak için formu doldurun.</p>
        </div>
        <button
          onClick={() => navigate('/refunds')}
          className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg flex items-center hover:bg-gray-200"
        >
          <ArrowLeft size={16} className="mr-1" />
          Geri Dön
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ödeme Yöntemi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ödeme Yöntemi *
                </label>
                <select
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Seçiniz</option>
                  {paymentMethods.map(method => (
                    <option key={method.id} value={method.id}>{method.name}</option>
                  ))}
                </select>
              </div>

              {/* İade Sebebi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İade Sebebi *
                </label>
                <select
                  name="reason_id"
                  value={formData.reason_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Seçiniz</option>
                  {refundReasons.map(reason => (
                    <option key={reason.id} value={reason.id}>{reason.name}</option>
                  ))}
                </select>
              </div>

              {/* Kullanıcı ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  USER ID *
                </label>
                <input
                  type="text"
                  name="user_id"
                  value={formData.user_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Login ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Login ID *
                </label>
                <input
                  type="text"
                  name="login_id"
                  value={formData.login_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Üye İsmi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Üye İsmi *
                </label>
                <input
                  type="text"
                  name="member_name"
                  value={formData.member_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* İade Edilecek İsim */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İade Edilecek İsim *
                </label>
                <input
                  type="text"
                  name="refund_name"
                  value={formData.refund_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* İade IBAN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İade IBAN
                </label>
                <input
                  type="text"
                  name="banveri"
                  value={formData.banveri}
                  onChange={handleChange}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Tutar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tutar (TL) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  onKeyDown={(e) => {
                    // Sadece rakam, backspace, silme, ok tuşları, tab ve kopyala/yapıştır kombinasyonlarına izin ver
                    if (
                      !(
                        (e.key >= '0' && e.key <= '9') ||
                        ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key) ||
                        (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase()))
                      )
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>

              {/* Durum */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durum *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Seçiniz</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>{status.name}</option>
                  ))}
                </select>
              </div>

              {/* Ticket-Chat ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket-Chat ID *
                </label>
                <input
                  type="text"
                  name="ticket_id"
                  value={formData.ticket_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Dekont Yükleme */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dekont Fotoğrafı
                </label>
                <div 
                  ref={dropzoneRef}
                  className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md transition-colors duration-200 ease-in-out"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    {receiptPreview ? (
                      <div>
                        {receiptPreview === 'pdf' ? (
                          <div className="mx-auto h-32 w-32 flex items-center justify-center bg-gray-100 rounded-md">
                            <span className="text-gray-500">PDF Dosyası</span>
                          </div>
                        ) : (
                          <img 
                            src={receiptPreview} 
                            alt="Dekont Önizleme" 
                            className="mx-auto h-32 object-contain"
                          />
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Dekont yüklendi.{' '}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({...formData, receipt_image: null});
                              setReceiptPreview(null);
                            }}
                            className="text-primary-600 hover:text-primary-500"
                          >
                            Kaldır
                          </button>
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                        />
                        <div className="flex text-sm text-gray-600 justify-center">
                          <label
                            htmlFor="receipt-image"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                          >
                            <span>Dosya Yükle</span>
                            <input
                              id="receipt-image"
                              name="receipt-image"
                              type="file"
                              accept="image/*,.pdf"
                              className="sr-only"
                              onChange={handleFileChange}
                            />
                          </label>
                          <p className="pl-1">veya sürükleyip bırakın</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF, PDF - max 5MB</p>
                        <p className="text-xs text-gray-500">Ctrl+V ile panodan yapıştırabilirsiniz</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/refunds')}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <PlusCircle size={16} className="mr-1" />
                    İade Oluştur
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RefundCreatePage;